/**
 * eventLabels — the single source of truth for how a Sacred Timeline / activity
 * event TYPE is presented to the operator (human label + icon + color +
 * category).
 *
 * Historically every surface (TimelineEventCard, CompactEventList,
 * EventDetailPanel, ScheduledEventZone, toasts, …) hard-coded its own label map,
 * so the same event could read "Flagged" in one place, "Keyword Flag" in another,
 * and raw "activity.keyword_flagged" in a third. This module consolidates them.
 *
 * Two vocabularies are supported and resolve to the SAME meta:
 *   - namespaced Sacred Timeline types: "activity.keyword_flagged", "activity.app_launched"
 *   - legacy flat EventDatabase types:  "keyword_flag", "app_usage"
 *
 * Unknown types fall back to a clean title-cased label derived from the type
 * (last dot-segment, underscores → spaces, Title Case) — never the raw string.
 */

// Canonical entries, keyed by the namespaced Sacred Timeline type (plus a few
// legacy-only concepts that never got a namespaced form). `shortLabel` is used by
// dense surfaces (badges); it defaults to `label` when omitted.
export const EVENT_META = {
    // ── activity ──
    'activity.app_launched':     { icon: '📱', label: 'App Launch',  shortLabel: 'App',   category: 'activity', color: '#8b5cf6' },
    'activity.website_visited':  { icon: '🌐', label: 'Website',     shortLabel: 'Site',  category: 'activity', color: '#3b82f6' },
    'activity.image_viewed':     { icon: '🖼️', label: 'Image',       shortLabel: 'Img',   category: 'activity', color: '#ec4899' },
    'activity.video_viewed':     { icon: '🎬', label: 'Video',       shortLabel: 'Vid',   category: 'activity', color: '#ec4899' },
    'activity.clipboard_copied': { icon: '📋', label: 'Clipboard',   shortLabel: 'Clip',  category: 'activity', color: '#22c55e' },
    'activity.keystroke_word':   { icon: '⌨️', label: 'Typing',      shortLabel: 'Keys',  category: 'activity', color: '#06b6d4' },
    'activity.screenshot_taken': { icon: '📸', label: 'Screenshot',  shortLabel: 'Cap',   category: 'activity', color: '#6b7280' },
    'activity.keyword_flagged':  { icon: '🚩', label: 'Flagged',     shortLabel: 'Flag',  category: 'activity', color: '#dc2626' },
    'activity.media_flagged':    { icon: '🚩', label: 'Media Flagged', shortLabel: 'Flag', category: 'activity', color: '#dc2626' },
    'activity.content_flagged':  { icon: '🚩', label: 'Content Flagged', shortLabel: 'Flag', category: 'activity', color: '#dc2626' },
    'activity.idle_started':     { icon: '💤', label: 'Idle',        shortLabel: 'Idle',  category: 'activity', color: '#9ca3af' },
    'activity.idle_ended':       { icon: '👋', label: 'Return',      shortLabel: 'Back',  category: 'activity', color: '#16a34a' },
    'activity.app_focused':      { icon: '📱', label: 'App Focus',   shortLabel: 'App',   category: 'activity', color: '#8b5cf6' },
    'activity.process_launched': { icon: '⚙️', label: 'Process',     shortLabel: 'Proc',  category: 'activity', color: '#6b7280' },
    'activity.window_opened':    { icon: '📂', label: 'Window',      shortLabel: 'Win',   category: 'activity', color: '#8b5cf6' },
    'activity.window_closed':    { icon: '📁', label: 'Window Closed', shortLabel: 'Close', category: 'activity', color: '#8b5cf6' },
    'activity.site_interaction': { icon: '💬', label: 'Social',      shortLabel: 'Social', category: 'activity', color: '#1d9bf0' },
    'activity.browser_interaction': { icon: '🖱️', label: 'Browser Action', shortLabel: 'Act', category: 'activity', color: '#f59e0b' },

    // ── chat ──
    'chat.session_started':  { icon: '💬', label: 'Chat',     category: 'chat', color: '#9f8cc7' },
    'chat.session_ended':    { icon: '💬', label: 'Chat End', category: 'chat', color: '#9f8cc7' },
    'chat.message_sent':     { icon: '💬', label: 'Message',  category: 'chat', color: '#9f8cc7' },
    'chat.ai_initiation':    { icon: '🤖', label: 'AI Chat',  category: 'chat', color: '#9f8cc7' },
    'chat.ai_mode_changed':  { icon: '🤖', label: 'AI Mode',  category: 'chat', color: '#9f8cc7' },

    // ── journal ──
    'journal.prompt_assigned':  { icon: '📓', label: 'Journal',   category: 'journal', color: '#f59e0b' },
    'journal.entry_submitted':  { icon: '📓', label: 'Journal',   category: 'journal', color: '#f59e0b' },
    'journal.entry_evaluated':  { icon: '📓', label: 'Evaluated', category: 'journal', color: '#f59e0b' },
    'journal.entry_rejected':   { icon: '📓', label: 'Rejected',  category: 'journal', color: '#dc2626' },
    'journal.rewrite_demanded': { icon: '📓', label: 'Rewrite',   category: 'journal', color: '#dc2626' },

    // ── interrogation ──
    'interrogation.session_started':  { icon: '❓', label: 'Interrogation', category: 'interrogation', color: '#ef4444' },
    'interrogation.session_ended':    { icon: '❓', label: 'Interrogation', category: 'interrogation', color: '#ef4444' },
    'interrogation.verdict_rendered': { icon: '⚖️', label: 'Verdict',       category: 'interrogation', color: '#ef4444' },

    // ── economy ──
    'economy.balance_adjusted': { icon: '💎', label: 'Economy', category: 'economy', color: '#10b981' },
    'economy.penalty_applied':  { icon: '💎', label: 'Penalty', category: 'economy', color: '#dc2626' },

    // ── challenges ──
    'challenge.assigned':  { icon: '🎯', label: 'Challenge', category: 'challenges', color: '#f97316' },
    'challenge.completed': { icon: '🎯', label: 'Completed', category: 'challenges', color: '#16a34a' },
    'challenge.failed':    { icon: '🎯', label: 'Failed',    category: 'challenges', color: '#dc2626' },

    // ── spin wheel ──
    'wheel.spin_scheduled':    { icon: '🎡', label: 'Spin Wheel', category: 'spin-wheel', color: '#8b5cf6' },
    'wheel.spin_executed':     { icon: '🎡', label: 'Spun',       category: 'spin-wheel', color: '#8b5cf6' },
    'wheel.outcome_delivered': { icon: '🎡', label: 'Outcome',    category: 'spin-wheel', color: '#8b5cf6' },

    // ── punishment ──
    'punishment.shock_delivered':        { icon: '⚡', label: 'Shock',      category: 'punishment', color: '#e53e3e' },
    'punishment.lockout_started':        { icon: '🔒', label: 'Lockout',    category: 'punishment', color: '#e53e3e' },
    'punishment.lockout_ended':          { icon: '🔓', label: 'Unlocked',   category: 'punishment', color: '#16a34a' },
    'punishment.write_lines_assigned':   { icon: '✍️', label: 'Write Lines', category: 'write-lines', color: '#f59e0b' },
    'punishment.write_lines_completed':  { icon: '✍️', label: 'Lines Done', category: 'write-lines', color: '#16a34a' },
    'punishment.input_sabotaged':        { icon: '🖱️', label: 'Sabotage',   category: 'punishment', color: '#e53e3e' },

    // ── time limit ──
    'limit.threshold_hit': { icon: '⏱️', label: 'Limit Hit', category: 'time-limit', color: '#d97706' },
    'limit.app_blocked':   { icon: '🚫', label: 'Blocked',   category: 'time-limit', color: '#dc2626' },
    'limit.app_unblocked': { icon: '✅', label: 'Unblocked', category: 'time-limit', color: '#16a34a' },
    'limit.file_blocked':  { icon: '🚫', label: 'File Blocked', category: 'time-limit', color: '#dc2626' },

    // ── AI ──
    'ai.dossier_updated':        { icon: '🔍', label: 'Profile',    category: 'ai', color: '#9f8cc7' },
    'ai.pattern_detected':       { icon: '🧠', label: 'Pattern',    category: 'ai', color: '#9f8cc7' },
    'ai.secret_discovered':      { icon: '🔮', label: 'Secret',     category: 'ai', color: '#9f8cc7' },
    'ai.emotional_model_shifted':{ icon: '💭', label: 'Emotion',    category: 'ai', color: '#9f8cc7' },
    'ai.directive_selected':     { icon: '🤖', label: 'Directive',  category: 'ai', color: '#9f8cc7' },
    'ai.action_planned':         { icon: '🤖', label: 'AI Plan',    category: 'ai', color: '#9f8cc7' },
    'ai.suggestion_generated':   { icon: '💡', label: 'Suggestion', category: 'suggestion', color: '#f59e0b' },

    // ── inquisitor (operator actions) ──
    'inquisitor.viewed_activity':        { icon: '👤', label: 'Viewed',    category: 'inquisitor', color: '#5c5650' },
    'inquisitor.viewed_keylogs':         { icon: '👤', label: 'Keylogs',   category: 'inquisitor', color: '#5c5650' },
    'inquisitor.sent_chat_message':      { icon: '👤', label: 'Chat',      category: 'inquisitor', color: '#5c5650' },
    'inquisitor.triggered_shock':        { icon: '👤', label: 'Shock',     category: 'inquisitor', color: '#5c5650' },
    'inquisitor.started_lockout':        { icon: '👤', label: 'Lockout',   category: 'inquisitor', color: '#5c5650' },
    'inquisitor.assigned_challenge':     { icon: '👤', label: 'Challenge', category: 'inquisitor', color: '#5c5650' },
    'inquisitor.assigned_journal':       { icon: '👤', label: 'Journal',   category: 'inquisitor', color: '#5c5650' },
    'inquisitor.accepted_ai_suggestion': { icon: '👤', label: 'Accepted',  category: 'inquisitor', color: '#16a34a' },
    'inquisitor.dismissed_ai_suggestion':{ icon: '👤', label: 'Dismissed', category: 'inquisitor', color: '#8a847c' },
    'inquisitor.approved_ai_action':     { icon: '👤', label: 'Approved',  category: 'inquisitor', color: '#16a34a' },
    'inquisitor.cancelled_ai_action':    { icon: '👤', label: 'Cancelled', category: 'inquisitor', color: '#dc2626' },
    'inquisitor.scheduled_action':       { icon: '👤', label: 'Scheduled', category: 'inquisitor', color: '#0284c7' },

    // ── system ──
    'system.connected':    { icon: '🔗', label: 'Connected',    category: 'system', color: '#16a34a' },
    'system.disconnected': { icon: '🔌', label: 'Disconnected', category: 'system', color: '#dc2626' },

    // ── consent ──
    'consent.granted':                { icon: '✅', label: 'Consent Granted',        category: 'system', color: '#16a34a' },
    'consent.renewed':                { icon: '🔄', label: 'Consent Renewed',        category: 'system', color: '#16a34a' },
    'consent.revoked':                { icon: '🛑', label: 'Consent Revoked',        category: 'system', color: '#dc2626' },
    'consent.self_release_started':   { icon: '⏳', label: 'Self-Release Started',   category: 'system', color: '#d97706' },
    'consent.self_release_completed': { icon: '🔓', label: 'Self-Release Completed', category: 'system', color: '#d97706' },
    'consent.self_release_cancelled': { icon: '↩️', label: 'Self-Release Cancelled', category: 'system', color: '#6b7280' },
    'consent.grace_started':          { icon: '⚠️', label: 'Consent Grace',          category: 'system', color: '#d97706' },
    'consent.expired':                { icon: '🚫', label: 'Consent Expired',        category: 'system', color: '#dc2626' },
    'consent.expiring_soon':          { icon: '⏰', label: 'Consent Expiring',       category: 'system', color: '#d97706' },
    'consent.app_quit':               { icon: '🚪', label: 'App Quit',               category: 'system', color: '#dc2626' },
};

// Legacy flat EventDatabase type → canonical namespaced key. Both resolve to the
// same meta so old and new surfaces read identically.
export const LEGACY_ALIASES = {
    app_usage:           'activity.app_launched',
    app_launch:          'activity.app_launched',
    website_visit:       'activity.website_visited',
    image_view:          'activity.image_viewed',
    image_viewed:        'activity.image_viewed',
    video_view:          'activity.video_viewed',
    video_viewed:        'activity.video_viewed',
    clipboard_copy:      'activity.clipboard_copied',
    keystroke:           'activity.keystroke_word',
    screenshot:          'activity.screenshot_taken',
    keyword_flag:        'activity.keyword_flagged',
    idle_start:          'activity.idle_started',
    idle_end:            'activity.idle_ended',
    process_launch:      'activity.process_launched',
    window_open:         'activity.window_opened',
    window_close:        'activity.window_closed',
    site_interaction:    'activity.site_interaction',
    browser_interaction: 'activity.browser_interaction',
    screen_lock:         'punishment.lockout_started',
    screen_unlock:       'punishment.lockout_ended',
    pishock_command:     'punishment.shock_delivered',
    app_blocked:         'limit.app_blocked',
    file_blocked:        'limit.file_blocked',
    file_access_blocked: 'limit.file_blocked',
};

/** Title-case the human part of an event type: "activity.keyword_flagged" → "Keyword Flagged". */
export function humanizeEventType(type) {
    if (!type || typeof type !== 'string') return 'Event';
    return type
        .split('.').pop()
        .split('_')
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ') || 'Event';
}

const FALLBACK_META = { icon: '📌', category: 'other', color: '#8a847c' };

/**
 * Resolve any event type (namespaced or legacy) to its display meta
 * { icon, label, shortLabel, category, color }. Unknown types get a clean
 * title-cased label and neutral styling — never the raw string.
 */
export function getEventMeta(type) {
    const direct = type && EVENT_META[type];
    const viaAlias = !direct && type && EVENT_META[LEGACY_ALIASES[type]];
    const meta = direct || viaAlias;
    if (meta) {
        return { shortLabel: meta.label, ...meta };
    }
    const label = humanizeEventType(type);
    return { ...FALLBACK_META, label, shortLabel: label };
}

/** Just the human label for an event type. */
export function getEventLabel(type) {
    return getEventMeta(type).label;
}

/**
 * Summarize a comma-joined list of source/interaction types (e.g. the dossier
 * analysis `source_type` "app_usage, app_usage, keystrokes, app_usage") into a
 * deduplicated, humanized, count-tagged string: "App Usage ×3 · Keystrokes".
 *
 * Uses humanizeEventType (plain Title Case) rather than getEventLabel so tokens
 * read accurately ("App Usage", not the timeline's terse "App Launch") and
 * dossier-only types (chat, personality_drift…) still format cleanly.
 *
 * @param {string} sourceType comma-joined type list
 * @param {object} [opts]
 * @param {number} [opts.max=5] cap distinct types shown; the rest collapse to "+N more"
 * @returns {string}
 */
export function summarizeSourceType(sourceType, { max = 5 } = {}) {
    if (!sourceType || typeof sourceType !== 'string') return '';
    const counts = new Map();
    for (const raw of sourceType.split(',')) {
        let token = raw.trim();
        if (!token) continue;
        // New source format already carries counts ("app_usage ×12"); respect
        // them. Legacy rows repeat the bare token, so default to 1 and let the
        // Map accumulate the duplicates. Handles × and x.
        let n = 1;
        const m = token.match(/[×x]\s*(\d+)\s*$/i);
        if (m) {
            n = parseInt(m[1], 10) || 1;
            token = token.slice(0, m.index).trim();
        }
        if (!token) continue;
        const label = humanizeEventType(token);
        counts.set(label, (counts.get(label) || 0) + n);
    }
    if (counts.size === 0) return '';
    const ordered = [...counts.entries()].sort((a, b) => b[1] - a[1]);
    const shown = ordered.slice(0, max).map(([label, n]) => (n > 1 ? `${label} ×${n}` : label));
    if (ordered.length > max) shown.push(`+${ordered.length - max} more`);
    return shown.join(' · ');
}
